import type Handlebars from 'handlebars'
import type { Cart, CartContent } from '@altazion/commerce-sdk-core'

type HandlebarsInstance = typeof Handlebars

/**
 * Helpers Handlebars liés au panier.
 *
 * Nécessite un getter réactif sur le panier actuel.
 * Usage :
 *   Nombre d'articles : {{cartCount}}
 *   Total : {{cartTotal}}
 *   {{#if cartHasItems}} ... {{/if}}
 */
export function registerCartHelpers(
  hbs: HandlebarsInstance,
  getCart: () => Cart | null,
  defaults: { locale: string; currency: string }
): void {
  hbs.registerHelper('cartCount', function (): number {
    const cart = getCart()
    if (!cart) return 0
    return cart.totalQuantity
  })

  hbs.registerHelper('cartTotal', function (): string {
    const cart = getCart()
    if (!cart) return ''
    return new Intl.NumberFormat(defaults.locale, {
      style: 'currency',
      currency: defaults.currency,
    }).format(cart.totalAmountWithTax)
  })

  hbs.registerHelper('cartTotalRaw', function (): number {
    const cart = getCart()
    return cart?.totalAmountWithTax ?? 0
  })

  hbs.registerHelper('cartHasItems', function (this: unknown, options: Handlebars.HelperOptions): string {
    const cart = getCart()
    const hasItems = cart ? cart.totalQuantity > 0 : false
    return hasItems ? options.fn(this as object) : options.inverse(this as object)
  })

  hbs.registerHelper('cartLineCount', function (): number {
    const cart = getCart()
    if (!cart?.content) return 0
    return cart.content.reduce((sum: number, group: CartContent) => sum + (group.lines?.length ?? 0), 0)
  })
}
